import { Injectable } from '@angular/core';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, delay, filter, finalize, map, mergeMap, share } from 'rxjs/operators';

import { SiteV2 } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { BusinessChatService } from 'src/app/shared/strategies/channels';
import { UserProfileService } from 'src/app/user-profile/user-profile.service';
import { environment } from 'src/environments/environment';
import { AlcmeonContextService } from '../../core/alcmeon-context/alcmeon-context.service';
import {
  ActionType,
  DELETE_ACTIONS,
  WebSocketActionNotification,
  WebSocketNotification,
  WebSocketNotificationType,
  WebSocketTaskNotification,
} from '../../core/websocket/model';
import { WebSocketService } from '../../core/websocket/websocket.service';
import { Site, TaskState } from '../../shared/models/misc.model';
import { ChunksAndIds } from '../chunks-and-ids.class';
import {
  taskIdFromTaskOrActionId,
  taskOrActionIdFromAnnotationActionId,
  taskOrActionIdFromAnswerActionId,
  taskOrActionIdFromHelpAnswerActionId,
  taskOrActionIdFromHelpRequestActionId,
  taskOrActionIdFromTaskId,
} from './id-conversions';
import {
  AnyTaskOrActionType,
  GenericAction,
  LazyloadTasksOrActionsResponse,
  Task,
  TaskForProcessingResponse,
  TaskOrActionType,
} from './model';
import { AIAssistantAnswerReliabilityEnum } from './models/ai-assistant-answer-reliability.enum';
import { TaskProcessContext } from './task-process-context.class';
import { TaskProcessService } from './task-process.service';

export const MESSAGE_PAGE_SIZE = 20;

/**
 * Handles the insertion of new bubbles in the current conversation
 * when we receive a websocket notification that an action has been performed.
 *
 * Note that updates of task state status message are handled by the message bubbles themselves,
 * this classes merely exposes them after adjusting the task state codes
 */
@Injectable({
  providedIn: 'root',
})
export class TaskProcessUpdatesService {
  /**
   * Emits a task-or-action when the backend notifies us that has been performed an action
   * which warrants inserting a new bubble in the conversation; that is, an action which
   * - is related to the current converation (as determined by the site id and "from user" id of the websocket notification)
   * - is an action of a relevant type (task created, execute answer, annotate, help request/answer)
   */
  newTaskOrAction$: Observable<AnyTaskOrActionType>;

  /**
   * Emits when the currently processed task's message has been deleted on the social network
   */
  processedMessageHasBeenDeleted$: Observable<WebSocketActionNotification>;

  /**
   * Emits a task-or-action when it has been lazy loaded.
   * A task-or-action is reloaded if we received a notification telling us it has changed
   * and it was already displayed in the conversation.
   * In addition, if we reload the currently processed task, we request the tag values to be included in the update
   */
  reloadedTask$: Observable<AnyTaskOrActionType>;

  private taskProcessContext: TaskProcessContext;
  private chunksAndIds: ChunksAndIds;

  private appleMessagesClosedConversationSubscriptions = new Subscription();

  constructor(
    wsService: WebSocketService,
    private basicService: BasicService,
    private alcmeonContextService: AlcmeonContextService,
    private taskProcessService: TaskProcessService,
    private webSocketService: WebSocketService,
    private businessChatService: BusinessChatService,
    private userProfileService: UserProfileService
  ) {
    this.initNewTaskOrAction$(wsService);
    this.initProcessedMessageHasBeenDeleted$(wsService);
    this.initReloadedTask$(wsService);

    this.newTaskOrAction$.subscribe(newTaskOrAction => {
      this.insertNewTaskOrActionOrReloadPage(newTaskOrAction);
    });

    this.reloadedTask$.subscribe(reloadedTaskOrAction => {
      this.replaceTaskIfAlreadyLoaded(reloadedTaskOrAction);
      if (this.isProcessedTaskId(reloadedTaskOrAction.id)) {
        this.taskProcessContext.updateOnProcessedTaskReloaded(reloadedTaskOrAction as Task);
      }
    });
  }

  setContext(taskProcessContext: TaskProcessContext, chunksAndIds: ChunksAndIds) {
    this.taskProcessContext = taskProcessContext;
    if (
      this.businessChatService.getType() ===
      ((taskProcessContext.site as Site).typeInt || (taskProcessContext.site as SiteV2).type)
    ) {
      this.appleMessagesClosedConversationSubscriptions?.unsubscribe();
      this.appleMessagesClosedConversationSubscriptions = new Subscription();
      this.handleAppleMessagesEvents(taskProcessContext);
    }
    this.chunksAndIds = chunksAndIds;
  }

  rattachAnswerToAIAssistantSuggestedAnswer(id: string, suggestion: string, rejected?: boolean) {
    this.taskProcessContext.aiAssistantSuggestedAnswer = {
      id,
      suggestion,
      status: rejected ? AIAssistantAnswerReliabilityEnum.REJECTED : null,
    };
  }

  private handleAppleMessagesEvents(taskProcessContext) {
    this.appleMessagesClosedConversationSubscriptions.add(
      this.taskProcessContext.isConversationClosedBS.subscribe((closed: boolean) => {
        if (closed) {
          this.insertNewTaskOrActionOrReloadPage({
            id: ':',
            type: TaskOrActionType.Task,
            closed: true,
            isPrivate: true,
            isDeleted: false,
            detailedTaskType: 'event',
            fromUserAvatarUrl: taskProcessContext?.from?.avatarURL,
            text: this.basicService.translate('api/bzc-conversation-closed-by-user'),
          } as any);
        } else {
          try {
            if (this.chunksAndIds?.loadedChunks) {
              // The last chunk if the closed conversation message introduced after the init.
              const lastChunk = this.chunksAndIds.loadedChunks[this.chunksAndIds.loadedChunks.length - 1];
              if (lastChunk.length === 1 && lastChunk[0].id === ':') {
                this.chunksAndIds.loadedChunks.pop();
              }
            }
          } catch {
            // Unable to remove the close conversation message.
          }
        }
      })
    );

    this.userProfileService
      .getUserProfile(this.taskProcessContext.from.id)
      .subscribe(user => this.taskProcessContext?.isConversationClosedBS.next(user.details.is_conversation_closed));

    this.appleMessagesClosedConversationSubscriptions.add(
      this.webSocketService.message$
        .pipe(
          filter(
            (event: any) =>
              this.taskProcessContext && event.type === WebSocketNotificationType.AppleMessagesCloseConversation
          )
        )
        .subscribe({
          next: event => {
            const { has_close_conversation, user_external_id, site_id } = event;
            const { from, site } = this.taskProcessContext;
            if (site.id === site_id && from?.external_id === user_external_id) {
              this.taskProcessContext.isConversationClosedBS.next(has_close_conversation);
            }
          },
        })
    );
  }

  clearContext(taskProcessContext: TaskProcessContext, chunksAndIds: ChunksAndIds) {
    // Avoid race conditions if a new TaskProcessPage component is created before a previous one is ngDestroyed
    if (this.taskProcessContext === taskProcessContext) {
      this.taskProcessContext = null;
    }
    if (this.chunksAndIds === chunksAndIds) {
      this.chunksAndIds = null;
    }
  }

  getProcessedTaskId(): number {
    try {
      return +this.taskProcessContext.processedTaskOrActionId.split(':')[0];
    } catch {
      return undefined;
    }
  }

  // Returns true when the task process page is displayed, and false when it is not
  private hasContext() {
    return !!this.taskProcessContext && !!this.chunksAndIds;
  }

  private initNewTaskOrAction$(wsService: WebSocketService) {
    this.newTaskOrAction$ = wsService.message$.pipe(
      delay(1000), // give the task process page a chance to have ngDestroyed
      filter(isActionWebSocketNotif),
      filter(notif => this.isActionNotifForCurrentConversation(notif)),
      filter(notif => !!this.taskOrActionIdForWsActionNotif(notif)),
      map(notif => this.taskOrActionIdForWsActionNotif(notif)),
      mergeMap(taskOrActionId => this.taskProcessService.lazyloadTasksForProcessing([taskOrActionId])),
      map(response => this.firstTaskOrAction(response)),
      share()
    ); // avoid duplicate http requests
  }

  private initProcessedMessageHasBeenDeleted$(wsService: WebSocketService) {
    this.processedMessageHasBeenDeleted$ = wsService.message$.pipe(
      filter(isActionWebSocketNotif),
      filter(notif => this.hasProcessedMessageBeenDeleted(notif))
    );
  }

  private initReloadedTask$(wsService: WebSocketService) {
    this.reloadedTask$ = wsService.message$.pipe(
      delay(1000), // give the task process page a chance to have ngDestroyed
      filter(isTaskWebSocketNotif),
      map(taskNotif => taskOrActionIdFromTaskId(taskNotif.task_id)),
      filter(taskOrActionId => this.isAlreadyLoaded(taskOrActionId)),
      mergeMap(taskOrActionId => this.taskProcessService.lazyloadTasksForProcessing([taskOrActionId], true)),
      map(response => this.firstTaskOrAction(response)),
      share()
    ); // avoid duplicate http requests
  }

  private firstTaskOrAction(response: LazyloadTasksOrActionsResponse) {
    const tasksOrActionsById = response.tasksOrActionsById;
    const id = Object.keys(tasksOrActionsById)[0];
    return tasksOrActionsById[id];
  }

  private isAlreadyLoaded(taskOrActionId: string) {
    return this.hasContext() && this.chunksAndIds.isLoaded(taskOrActionId);
  }

  private insertNewTaskOrActionOrReloadPage(newTaskOrAction: AnyTaskOrActionType) {
    if (
      !environment.bundledWithAngularJSApp &&
      this.hasContext() &&
      newTaskOrAction.type === TaskOrActionType.Task &&
      this.chunksAndIds.shouldReloadPageWhenNewTaskArrives() &&
      this.taskProcessContext?.areAllEditorsEmpty() &&
      newTaskOrAction.id !== ':' &&
      !this.taskProcessContext.isConversationClosedBS.value
    ) {
      this.taskProcessService.reloadTaskProcessStateWithNewTask(
        TaskState.Pending,
        taskIdFromTaskOrActionId(newTaskOrAction.id),
        this.taskProcessContext.isCRMMode
      );
    } else if (this.hasContext()) {
      this.insertNewTaskOrAction(newTaskOrAction);
    }
  }

  private insertNewTaskOrAction(newTaskOrAction: AnyTaskOrActionType) {
    this.chunksAndIds.insertNewTaskOrAction(newTaskOrAction);
    if (this.shouldTryToSwitchProcessingTask(newTaskOrAction)) {
      // 3784 - If a new private task arrives while we are processing a private task,
      // let's automatically archive the current task, acquire the new task,
      // and update the context so that the user automatically switches to the new task
      this.archiveCurrentTaskAndStartProcessingNewTask(newTaskOrAction);
    } else if (
      newTaskOrAction.type === TaskOrActionType.Task ||
      !this.alcmeonContextService.isMe((newTaskOrAction as GenericAction).adminUser)
    ) {
      // If the new taskOrAction is a task or is an action performed by another admin user,
      // let's display the yellow 'new messages below' button
      this.chunksAndIds.showNewMessagesBelowButon();
    } else {
      // if we don't display the yellow button, let's at least scroll to the bottom
      // so that the user realizes his action has been added to the conversation
      this.taskProcessContext.scrollToBottom();
    }
  }

  /**
   * We should auto-switch to a newly recieved task if both are private
   * and the currently processed task is assigned to the logged user
   */
  private shouldTryToSwitchProcessingTask(newTaskOrAction) {
    return (
      this.hasContext() &&
      newTaskOrAction.type === TaskOrActionType.Task &&
      (newTaskOrAction as Task).isPrivate &&
      this.taskProcessContext.isPrivate() &&
      this.taskProcessContext.isAssignedToMe()
    );
  }

  private archiveCurrentTaskAndStartProcessingNewTask(newTaskOrAction) {
    this.basicService.showSpinner();
    const currentTaskId = this.taskProcessContext.processedTaskId;
    const newTaskId = taskIdFromTaskOrActionId(newTaskOrAction.id);
    this.taskProcessService
      .archiveCurrentAndStartProcessingNewTask(currentTaskId, newTaskId)
      .pipe(
        finalize(() => this.basicService.hideSpinner({ restoreFocusAfterMs: 500 })),
        catchError(error => {
          this.chunksAndIds?.showNewMessagesBelowButon();
          return throwError(error);
        })
      )
      .subscribe((response: TaskForProcessingResponse) => {
        // The user started to leave the PP without waiting the answer.
        if (this.taskProcessContext && response) {
          this.taskProcessContext.processedTaskId = newTaskId;
          this.taskProcessContext.updateWithTask(response);
          this.taskProcessContext.scrollToBottom();
        }
      });
  }

  private replaceTaskIfAlreadyLoaded(reloadedTaskOrAction) {
    if (this.hasContext()) {
      this.chunksAndIds.replaceTaskOrActionIfAlreadyLoaded(reloadedTaskOrAction);
    }
  }

  private isActionNotifForCurrentConversation(notif: WebSocketNotification): boolean {
    // this.taskProcessContext can be undefined if we receive a notification about us just acquiring the task
    // faster than the call to /task/search
    return (
      notif.type === WebSocketNotificationType.Action &&
      this.hasContext() &&
      (notif as WebSocketActionNotification).site_id === this.taskProcessContext.site.id &&
      (notif as WebSocketActionNotification).from_user_id === this.taskProcessContext.from?.id
    );
  }

  private taskOrActionIdForWsActionNotif(notif: WebSocketActionNotification): string {
    switch (notif.action_type) {
      case ActionType.TASK_CREATED:
        return taskOrActionIdFromTaskId(notif.task_id);
      case ActionType.EXECUTE_ANSWER:
        return taskOrActionIdFromAnswerActionId(notif.log_answer_action_id);
      case ActionType.COMMENT:
        return taskOrActionIdFromAnnotationActionId(notif.action_id);
      case ActionType.HELP_REQUEST:
        return taskOrActionIdFromHelpRequestActionId(notif.action_id);
      case ActionType.HELP_ANSWER:
        return taskOrActionIdFromHelpAnswerActionId(notif.action_id);
      default:
        return null;
    }
  }

  private hasProcessedMessageBeenDeleted(notif: WebSocketActionNotification): boolean {
    return DELETE_ACTIONS.includes(notif.action_type) && this.isActionNotifAboutProcessedTask(notif);
  }

  private isActionNotifAboutProcessedTask(notif: WebSocketActionNotification) {
    return this.hasContext() && notif.task_id === this.taskProcessContext.processedTaskId;
  }

  private isProcessedTaskId(taskOrActionId: string) {
    return this.hasContext() && taskOrActionId === this.taskProcessContext.processedTaskOrActionId;
  }
}

//
// helper functions outside of class so that we can use their type predicate signature
// in a .filter() to narrow the observable's type
//

function isTaskWebSocketNotif(notif: WebSocketNotification): notif is WebSocketTaskNotification {
  return notif.type === WebSocketNotificationType.Task;
}

function isActionWebSocketNotif(notif: WebSocketNotification): notif is WebSocketActionNotification {
  return notif.type === WebSocketNotificationType.Action;
}
